import { useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";

const SLOTS = ["Prima Runa", "Seconda Runa", "Terza Runa"];

const Stone = ({ picked, ...props }) => (
  <button
    {...props}
    className="w-full aspect-square rounded-full border border-amber-500/25 bg-gradient-to-br from-[#3a1d5c] via-[#241040] to-[#150824] flex items-center justify-center shadow-[inset_0_2px_6px_rgba(0,0,0,0.6)] hover:border-amber-400/60 hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:hover:translate-y-0"
  >
    <span className="text-amber-400/40 text-sm select-none">ᚭ</span>
  </button>
);

export const RunePicker = ({ sessionId, totalCards, spread, onComplete }) => {
  const [picks, setPicks] = useState([]);
  const [order, setOrder] = useState(() => Array.from({ length: totalCards }, (_, i) => i));
  const [shaking, setShaking] = useState(false);
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(false);

  const shuffle = () => {
    if (busy) return;
    setShaking(true);
    setOrder((o) => [...o].sort(() => Math.random() - 0.5));
    setTimeout(() => setShaking(false), 700);
  };

  const pick = async (i) => {
    if (busy || picks.includes(i) || picks.length >= spread || doneRef.current) return;
    setBusy(true);
    try {
      await api.post(`/draw-sessions/${sessionId}/pick`, { index: i });
      const next = [...picks, i];
      setPicks(next);
      if (next.length >= spread && !doneRef.current) {
        doneRef.current = true;
        setTimeout(() => onComplete(), 900);
      }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <style>{`@keyframes runeShake { 0%,100%{transform:translateX(0) rotate(0)} 20%{transform:translateX(-6px) rotate(-2deg)} 40%{transform:translateX(6px) rotate(2deg)} 60%{transform:translateX(-4px) rotate(-1deg)} 80%{transform:translateX(4px) rotate(1deg)} }`}</style>

      <div className="flex items-center justify-center gap-3 mb-6">
        {SLOTS.map((label, i) => (
          <div key={label} className="text-center" data-testid={`rune-slot-${i}`}>
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full border flex items-center justify-center ${
              picks[i] !== undefined
                ? "border-amber-500/60 bg-gradient-to-br from-[#3a1d5c] to-[#150824] shadow-[0_0_20px_rgba(217,180,74,0.25)]"
                : "border-dashed border-purple-500/30 bg-[#0f0617]/50"
            }`}>
              {picks[i] !== undefined && <span className="text-amber-400/50 text-lg select-none">ᚭ</span>}
            </div>
            <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-2">{label}</p>
          </div>
        ))}
      </div>

      <div className="text-center mb-5">
        <button
          onClick={shuffle}
          disabled={busy || picks.length >= spread}
          data-testid="rune-shuffle-btn"
          className="text-xs text-amber-300/80 hover:text-amber-200 border border-amber-500/40 rounded-full px-5 py-2 transition-colors disabled:opacity-50"
        >
          Mescola il sacchetto
        </button>
      </div>

      <div
        data-testid="rune-bag"
        style={shaking ? { animation: "runeShake 0.7s ease-in-out" } : undefined}
        className="grid grid-cols-5 gap-2.5 sm:gap-3 max-w-md mx-auto rounded-[2.5rem] border border-amber-500/20 bg-gradient-to-b from-[#1c0d31] to-[#0f0617] p-5 sm:p-7 shadow-[inset_0_6px_24px_rgba(0,0,0,0.55)]"
      >
        {order.map((i) =>
          picks.includes(i) ? (
            <div key={i} className="w-full aspect-square rounded-full border border-dashed border-purple-500/20" data-testid={`rune-taken-${i}`} />
          ) : (
            <Stone key={i} onClick={() => pick(i)} disabled={busy || picks.length >= spread} data-testid={`rune-stone-${i}`} />
          )
        )}
      </div>
      <p className="text-center text-xs text-amber-300/80 mt-5 font-cinzel tracking-[0.2em] uppercase" data-testid="rune-picks-counter">
        {picks.length} di {spread} Rune scelte
      </p>
    </div>
  );
};
